import {Card, Parameter, Parameters} from 'platform/components';
import {HStack, Icon, Show, Text, VStack, type ThemeColorPath} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {CustomNoticeColor, CustomNoticeResponseBody} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {EMPTY_PLACEHOLDER, getUserName} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {CUSTOM_NOTICE_ICONS} from '../../constants/customNoticeIcons';

interface CustomNoticeCardBaseProps extends TestIdProps {
  notice: CustomNoticeResponseBody;
  parameters?: Parameter[];
  actions?: ReactNode;
}

export function CustomNoticeCardBase(props: CustomNoticeCardBaseProps) {
  const {data: author} = useGetUserQuery({id: props.notice.createdBy});
  const authorName = getUserName(author) ?? EMPTY_PLACEHOLDER;

  return (
    <Card
      variant="inlineGrey"
      colorStripe={props.notice.color}
      data-testid={suffixTestId('customNoticeCard', props)}
    >
      <HStack spacing={4} align="flex-start" justify="space-between">
        <HStack spacing={4} align="flex-start">
          <Icon
            value={CUSTOM_NOTICE_ICONS[props.notice.icon]}
            color={getCustomNoticeIconColor(props.notice.color)}
            size={6}
          />

          <VStack spacing={2}>
            <Text
              size="small"
              overflowWrap="anywhere"
              data-testid={suffixTestId('customNoticeCard-text', props)}
            >
              {props.notice.text}
            </Text>
            <Parameters
              parameters={[...(props.parameters ?? []), authorName]}
              size="xSmall"
              color="tertiary"
              parametersProps={{noWrap: false, overflowWrap: 'anywhere'}}
              data-testid={suffixTestId('customNoticeCard-metadata', props)}
            />
          </VStack>
        </HStack>

        <Show when={isNotNil(props.actions)}>{props.actions}</Show>
      </HStack>
    </Card>
  );
}

function getCustomNoticeIconColor(color: CustomNoticeColor) {
  return match<CustomNoticeColor, ThemeColorPath>(color)
    .with('neutral', () => 'palettes.neutral.300.100')
    .with('blue', () => 'palettes.blue.70.100')
    .with('teal', () => 'palettes.teal.60.100')
    .with('green', () => 'palettes.green.60.100')
    .with('orange', () => 'palettes.orange.60.100')
    .with('yellow', () => 'palettes.yellow.60.100')
    .with('magenta', () => 'palettes.magenta.60.100')
    .with('purple', () => 'palettes.purple.60.100')
    .with('red', () => 'palettes.red.60.100')
    .exhaustive();
}
